import { createSelector } from '@reduxjs/toolkit';

import { OrganizationStaff, OrganizationStaffRoleType } from 'protocol/model/dto_organization_staff_pb';

import { RootState } from '@/app/model/store';

import { organizationUserSharingInfoAdapter } from './userSlice';

export type StaffStatus = 'active' | 'deleted';

export const getStaffStatus = (staff: OrganizationStaff): StaffStatus => {
  if (staff.Status.case === 'Deleted') { return 'deleted'; }
  return 'active';
};

const statusOrder: Record<StaffStatus, number> = {
  active: 0,
  deleted: 1,
};

const roleOrder: Record<number, number> = {
  [OrganizationStaffRoleType.OrganizationStaffRoleType_FullAccess]: 0,
  [OrganizationStaffRoleType.OrganizationStaffRoleType_ClinicalAccess]: 1,
  [OrganizationStaffRoleType.OrganizationStaffRoleType_LimitedClinicalAccess]: 2,
  [OrganizationStaffRoleType.OrganizationStaffRoleType_NonClinicalMember]: 3,
  [OrganizationStaffRoleType.OrganizationStaffRoleType_InvalidValue]: 4,
};

const sharedFromSelectors = organizationUserSharingInfoAdapter.getSelectors(
  (state: RootState) => state.user,
);

export const { selectAll: selectAllSharedFromOrganizations } = sharedFromSelectors;

export const selectBillingAccountInfo = createSelector(
  (state: RootState) => state.user.billingAccount,
  (accountInfo) => accountInfo,
);

export const selectUserSettings = createSelector(
  (state: RootState) => state.user.settings,
  (userSettings) => userSettings,
);

export const selectAdminUser = (state: RootState) => state.user.user;

export const selectCurrentUser = (state: RootState) => state.user.currentUser;

export const selectCurrentUserLoadingState = (state: RootState) =>
  state.user.currentUserLoadingState;

export const selectCurrentUserOrganizations = (state: RootState) =>
  state.user.currentUserStaff;

export const selectCurrentUserOrganizationNames = (state: RootState) =>
  state.user.currentUserOrganizationNames;

export const selectSortedCurrentUserStaff = createSelector(
  selectCurrentUserOrganizations,
  (staff) =>
    [...staff].sort((a, b) => {
      const statusDiff = statusOrder[getStaffStatus(a)] - statusOrder[getStaffStatus(b)];
      if (statusDiff !== 0) { return statusDiff; }

      return (roleOrder[a.Role] ?? 4) - (roleOrder[b.Role] ?? 4);
    }),
);

export const selectAdminUserOrganizations = (state: RootState) =>
  state.user.adminUserOrganizations;

export const selectAdminDistributorOrganization = createSelector(
  selectAdminUserOrganizations,
  (organizations) =>
    organizations.find((organization) => Boolean(organization.Distributor)),
);

export const selectIsDistributorAdmin = createSelector(
  selectAdminDistributorOrganization,
  (distributorOrganization) => Boolean(distributorOrganization),
);

export const selectAdminDistributorEmail = createSelector(
  selectAdminUser,
  (adminUser) => adminUser?.PersonalData?.Emails?.[0] ?? '',
);

export const selectCanManageDistributors = createSelector(
  selectAdminUser,
  (adminUser) => adminUser?.YourPermissions?.CanManageDistributors ?? false,
);

export const selectCanManageDSO = createSelector(
  selectAdminUser,
  (adminUser) => adminUser?.YourPermissions?.CanManageDSO ?? false,
);
